<script setup lang="ts">
import { HomeKpiGrid, HomeQuickLinks, TraceIdAlert, useSession, type KpiCard, type QuickLink } from '@yc/ui-kit'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { getHomeStats, type HomeStats } from '../api/home'

const { me } = useSession()
const router = useRouter()

const stats = ref<HomeStats | null>(null)
const error = ref<unknown>(null)
const loading = ref(false)

const greeting = computed(() => {
  const h = new Date().getHours()
  if (h < 6) return '夜深了'
  if (h < 12) return '早上好'
  if (h < 14) return '中午好'
  if (h < 18) return '下午好'
  return '晚上好'
})

/** 四张 KPI 卡（spec §5）：点卡片跳到对应的业务页 */
const cards = computed<KpiCard[]>(() => [
  { key: 'asn', label: '今日入库单', value: stats.value?.today_asn ?? 0, to: '/inbound' },
  { key: 'order', label: '今日出库单', value: stats.value?.today_order ?? 0, to: '/outbound' },
  { key: 'task', label: '待拣任务', value: stats.value?.pending_tasks ?? 0, to: '/waves' },
  { key: 'sku', label: '库存 SKU 数', value: stats.value?.stock_skus ?? 0, to: '/stocks' },
])

// 不挂 perm：这 7 个入口此前就是无条件显示的，保持现状不改行为。
// 各路由的 meta.perm 仍在守卫里把关（没权限点进去会落到 403）。
const links: QuickLink[] = [
  { to: '/inbound', label: '入库单' },
  { to: '/outbound', label: '出库单' },
  { to: '/waves', label: '波次拣货' },
  { to: '/stocks', label: '库存查询' },
  { to: '/stocktakes', label: '盘点' },
  { to: '/reports', label: '报表中心' },
  { to: '/oplogs', label: '操作日志' },
]

async function load(): Promise<void> {
  loading.value = true
  error.value = null
  try {
    stats.value = await getHomeStats()
  } catch (e) {
    error.value = e
    stats.value = null
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())
</script>

<template>
  <div class="yc-home">
    <header class="yc-home__hero">
      <div>
        <h2 class="yc-home__greeting">{{ greeting }}，{{ me?.name || me?.username }}</h2>
        <p class="yc-home__sub">
          {{ me?.tenant?.name }}（{{ me?.tenant?.code }}）· {{ me?.username }}
        </p>
      </div>
      <el-button :loading="loading" @click="load">刷新</el-button>
    </header>

    <div v-if="error" class="yc-home__error">
      <TraceIdAlert :error="error" />
    </div>

    <HomeKpiGrid :cards="cards" :loading="loading" @select="router.push" />

    <HomeQuickLinks :links="links" @navigate="router.push" />
  </div>
</template>

<style scoped lang="scss">
.yc-home {
  &__hero {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--yc-space-4);
    margin-bottom: var(--yc-space-6);
  }

  &__greeting {
    margin: 0;
    font-size: var(--yc-font-2xl);
    font-weight: 600;
  }

  &__sub {
    margin: var(--yc-space-2) 0 0;
    color: var(--yc-text-secondary);
    font-size: var(--yc-font);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }
}
</style>
